import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';
import logo from '../../assets/styles/logo.png';
import {
  Home,
  QrCode,
  Users,
  UserPlus,
  CreditCard,
  Banknote,
  Send,
  DoorOpen,
  Bell,
  BarChart3,
  Settings,
  LogOut,
  X,
  User,
  Globe,
} from 'lucide-react';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: Home, end: true, permKey: 'dashboard' },
  { to: '/tarayici', label: 'QR-Scanner', icon: QrCode, permKey: 'qr_tarayici' },
  { to: '/uyeler', label: 'Mitglieder', icon: Users, permKey: 'uyeler' },
  { to: '/basvurular', label: 'Bewerbungen', icon: UserPlus, permKey: 'basvurular' },
  { to: '/aidatlar', label: 'Beiträge', icon: CreditCard, permKey: 'aidatlar' },
  { to: '/odemeler', label: 'Zahlungen', icon: Banknote, permKey: 'odemeler' },
  { to: '/versandprotokoll', label: 'Versandprotokoll', icon: Send, permKey: 'odemeler' },
  { to: '/giris-kayitlari', label: 'Eintrittsprotokolle', icon: DoorOpen, permKey: 'giris_kayitlari' },
  { to: '/duyurular', label: 'Ankündigungen', icon: Bell, permKey: 'duyurular' },
  { to: '/raporlar', label: 'Berichte', icon: BarChart3, permKey: 'raporlar' },
  { to: '/website-yonetimi', label: 'Website', icon: Globe, permKey: 'website' },
  { to: '/ayarlar', label: 'Einstellungen', icon: Settings, permKey: 'ayarlar' },
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);

  const hasAccess = (permKey) => {
    if (!permKey) return true;
    if (user?.role === 'baskan') return true;
    if (user?.permissions && user.permissions[permKey] === true) return true;
    return false;
  };

  // Yeni basvuru (bekleyen uye) sayisini cek -> Mitglieder menusunde rozet
  const canSeeApplications = user?.role === 'baskan' || user?.permissions?.basvurular === true;
  const fetchPending = useCallback(async () => {
    if (!canSeeApplications) return;
    try {
      const res = await api.get('/members/applications');
      const list = Array.isArray(res.data) ? res.data : (res.data.applications || res.data.data || []);
      setPendingCount(list.length);
    } catch (e) { /* yoksay */ }
  }, [canSeeApplications]);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 60000);
    return () => clearInterval(t);
  }, [fetchPending]);

  const linkClasses = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-gs-yellow text-gs-dark font-semibold'
        : 'text-white/80 hover:bg-white/15 hover:text-white'
    }`;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        style={{ height: '100dvh' }}
        className={`fixed top-0 left-0 z-50 h-screen w-64 bg-gs-red border-r border-white/10 flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="shrink-0 relative px-10 py-2">
          <img src={logo} alt="Logo" className="w-full block" style={{ margin: 0, display: 'block' }} />
          <button
            onClick={onClose}
            className="lg:hidden absolute right-2 top-2 p-1 rounded-md text-white/60 hover:text-white bg-black/20"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {navItems
            .filter((item) => hasAccess(item.permKey))
            .map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClasses}
                onClick={onClose}
              >
                <item.icon size={18} />
                <span className="flex-1">{item.label}</span>
                {(item.to === '/uyeler' || item.to === '/basvurular') && pendingCount > 0 && (
                  <span className="ml-auto bg-gs-yellow text-gs-dark text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {pendingCount}
                  </span>
                )}
              </NavLink>
            ))}
        </nav>

        {/* Bottom section - fixed height, won't be cut off */}
        <div className="shrink-0 border-t border-white/10 px-3 py-3 space-y-1">
          <NavLink
            to="/profil"
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={onClose}
          >
            <div className="w-8 h-8 rounded-full bg-gs-yellow/20 text-gs-yellow flex items-center justify-center text-xs font-bold shrink-0">
              {user?.first_name?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
            </div>
          </NavLink>
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            <span>Abmelden</span>
          </button>
        </div>
      </aside>
    </>
  );
}
